import React from 'react';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CategoryIcon from '@mui/icons-material/Category';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ExploreIcon from '@mui/icons-material/Explore';
import PanToolIcon from '@mui/icons-material/PanTool';
import PolylineOutlinedIcon from '@mui/icons-material/PolylineOutlined';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SvgIcon from '@mui/material/SvgIcon';
import { Box, IconButton, Paper, Tooltip } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import {
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_SEGMENT,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';

import FlexWrap from 'resources/flexWrap.svg?react';

import * as actions from '../actions';
import * as selectors from '../selectors';

const Toolbar = ({
    isDisabledEdition,
    isDisablingModules,
    isDrawingMode,
    isEditingSegment,
    isEditionMode,
    mapRef,
    onCloseDrawer,
    setAddSegmentModalOpenStatus,
    setDrawingMode,
    setIsDisablingModules,
}) => {
    const { t } = useTranslation();
    const isDisabled = isDrawingMode || isEditingSegment || isEditionMode;

    const handleClickAdd = ({ type = null } = {}) => {
        if (type === POLYGON_SEGMENT) setAddSegmentModalOpenStatus(true);
        else setDrawingMode(true, type);
        onCloseDrawer();
    };

    const handleClickDisabledModules = () => {
        setIsDisablingModules(!isDisablingModules);
        onCloseDrawer();
    };

    const handleClick = () => {
        setIsDisablingModules(false);
        setDrawingMode(false);
        onCloseDrawer();
    };

    const handleOnClickRotateCounterClockwise = (e) => {
        const map = mapRef.current.self();
        const heading = map.getHeading() || 0;
        let degrees = 1;
        if (e.shiftKey) degrees = degrees * 10;
        if (e.altKey) degrees = degrees * 0.1;
        if (e.shiftKey && e.altKey) degrees = degrees * 0.01;
        map.setHeading(heading + degrees);
    };
    const handleOnClickRotateClockwise = (e) => {
        const map = mapRef.current.self();
        const heading = map.getHeading() || 0;
        let degrees = -1;
        if (e.shiftKey) degrees = degrees * 10;
        if (e.altKey) degrees = degrees * 0.1;
        if (e.shiftKey && e.altKey) degrees = degrees * 0.01;
        map.setHeading(heading + degrees);
    };
    const handleOnClickRestoreRotation = () => {
        const map = mapRef.current.self();
        map.setHeading(0);
    };

    return (
        <Paper
            id="panel-layout-toolbar"
            elevation={0}
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mt: 2,
                mx: 2,
                p: 1,
            }}
        >
            <Tooltip title={t('Select')}>
                <Box>
                    <IconButton
                        disabled={isDisabledEdition || isEditingSegment}
                        onClick={handleClick}
                        size="small"
                    >
                        <PanToolIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Add segment')}>
                <Box>
                    <IconButton
                        disabled={
                            isDisabled ||
                            isDisabledEdition ||
                            isDisablingModules
                        }
                        onClick={() =>
                            handleClickAdd({ type: POLYGON_SEGMENT })
                        }
                        size="small"
                    >
                        <PolylineOutlinedIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Add obstacle')}>
                <Box>
                    <IconButton
                        disabled={
                            isDisabled ||
                            isDisabledEdition ||
                            isDisablingModules
                        }
                        onClick={() =>
                            handleClickAdd({ type: POLYGON_OBSTACLE })
                        }
                        size="small"
                    >
                        <CategoryIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Add circular obstacle')}>
                <Box id="panel-layout-toolbar-add-circular-obstacle-button">
                    <IconButton
                        disabled={isDisabled || isDisablingModules}
                        onClick={() => handleClickAdd({ type: POLYGON_CIRCLE })}
                        size="small"
                    >
                        <CircleOutlinedIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Add image')}>
                <Box>
                    <IconButton
                        disabled={
                            isDisabled ||
                            isDisabledEdition ||
                            isDisablingModules
                        }
                        onClick={() => handleClickAdd({ type: POLYGON_MAP })}
                        size="small"
                    >
                        <AddPhotoAlternateIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Enable/Disable modules')}>
                <Box>
                    <IconButton
                        disabled={isDisabled || isDisabledEdition}
                        onClick={handleClickDisabledModules}
                        size="small"
                    >
                        <SvgIcon fontSize="inherit">
                            <FlexWrap />
                        </SvgIcon>
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Rotate counter clockwise')}>
                <Box sx={{ ml: 'auto' }}>
                    <IconButton
                        disabled={isEditionMode || isEditingSegment}
                        onClick={handleOnClickRotateCounterClockwise}
                        size="small"
                    >
                        <RotateLeftIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Rotate clockwise')}>
                <Box>
                    <IconButton
                        disabled={isEditionMode || isEditingSegment}
                        onClick={handleOnClickRotateClockwise}
                        size="small"
                    >
                        <RotateRightIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>

            <Tooltip title={t('Restore rotation')}>
                <Box>
                    <IconButton
                        disabled={isEditionMode || isEditingSegment}
                        onClick={handleOnClickRestoreRotation}
                        size="small"
                    >
                        <ExploreIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            </Tooltip>
        </Paper>
    );
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isDisablingModules: selectors.getIsDisablingModules,
    isDrawingMode: selectors.getIsDrawingMode,
    isEditingSegment: selectors.getIsEditingSegment,
    offerPanelsCounters: selectors.getOfferPanelsCounters,
});

const mapDispatchToProps = (dispatch) => ({
    setAddSegmentModalOpenStatus: (isOpen) =>
        dispatch(actions.setAddSegmentModalOpenStatus(isOpen)),
    setDrawingMode: (isDrawingMode, type) =>
        dispatch(actions.setDrawingMode(isDrawingMode, type)),
    setIsDisablingModules: (isDisabling) =>
        dispatch(actions.setIsDisablingModules(isDisabling)),
});

Toolbar.propTypes = {
    isDisabledEdition: PropTypes.bool,
    isDisablingModules: PropTypes.bool,
    isDrawingMode: PropTypes.bool,
    isEditingSegment: PropTypes.bool,
    isEditionMode: PropTypes.bool,
    mapRef: PropTypes.object,
    onCloseDrawer: PropTypes.func,
    setAddSegmentModalOpenStatus: PropTypes.func,
    setDrawingMode: PropTypes.func,
    setIsDisablingModules: PropTypes.func,
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
)(Toolbar);
